import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { StatKey } from '../../../services/dashboard.service';

interface StatView {
  key: StatKey;
  label: string;
  value: string;
  detail: string;
  icon: string;
  tone: 'violet' | 'blue' | 'green' | 'orange';
  arrow: boolean;
  visible: boolean;
}

/**
 * Cuatro accesos informativos rápidos. No son gráficos: cada tarjeta muestra
 * un dato real y, cuando aporta, una comparación también real.
 */
@Component({
  selector: 'app-dashboard-stats',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="stats">
      <ng-container *ngFor="let stat of stats">
        <button
          *ngIf="stat.visible"
          type="button"
          class="stat"
          [ngClass]="'tone-' + stat.tone"
          [attr.aria-label]="stat.label + ': ' + stat.value + '. ' + stat.detail"
          (click)="select.emit(stat.key)"
        >
          <span class="icon" aria-hidden="true"><i class="bi" [ngClass]="stat.icon"></i></span>
          <span class="body">
            <span class="label">{{ stat.label }}</span>
            <strong [class.is-placeholder]="stat.value === '—'">{{ stat.value }}</strong>
            <span class="detail" [class.has-arrow]="stat.arrow">{{ stat.detail }}<i *ngIf="stat.arrow" class="bi bi-arrow-right" aria-hidden="true"></i></span>
          </span>
        </button>
      </ng-container>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .85rem; }
    .stat {
      display: flex; align-items: center; gap: .8rem; min-height: 92px; padding: .95rem 1rem;
      text-align: left; font: inherit; color: var(--ts-text); cursor: pointer;
      position: relative; overflow: hidden; border: 1px solid var(--ts-card-border, var(--ts-border)); border-radius: var(--ts-radius-md);
      background: linear-gradient(145deg, var(--ts-surface), var(--ts-surface-soft)); box-shadow: var(--ts-card-shadow, var(--ts-shadow-sm));
      transition: transform var(--ts-transition), border-color var(--ts-transition), background var(--ts-transition);
    }
    .stat:hover { transform: translateY(-2px); border-color: var(--ts-border-blue-400-a40); background: var(--ts-surface-hover); }
    .stat::after { content: ''; position: absolute; inset: auto 0 0; height: 3px; background: var(--stat-accent, var(--ts-primary)); opacity: .8; }
    .stat:focus-visible { outline: 3px solid var(--ts-border-blue-400-a28-2); outline-offset: 2px; }
    .icon { display: grid; place-items: center; width: 46px; height: 46px; flex: 0 0 46px; border-radius: 14px; font-size: 1.15rem; }
    .body { display: grid; gap: .1rem; min-width: 0; }
    .label { color: var(--ts-text-muted); font-size: .71rem; font-weight: 650; }
    strong { color: var(--ts-text); font-family: "Plus Jakarta Sans", Inter, sans-serif; font-size: 1.5rem; line-height: 1.1; letter-spacing: -.03em; }
    strong.is-placeholder { color: var(--ts-text-subtle); }
    .detail { overflow: hidden; color: var(--ts-text-subtle); font-size: .66rem; text-overflow: ellipsis; white-space: nowrap; }
    .detail.has-arrow { display: inline-flex; align-items: center; gap: .3rem; color: var(--ts-text-blue-300-4); font-weight: 700; }
    .tone-violet { --stat-accent: #7c3aed; } .tone-violet .icon { color: var(--ts-text-indigo-200); background: var(--ts-bg-indigo-350-a15); }
    .tone-blue { --stat-accent: #2563eb; } .tone-blue .icon { color: var(--ts-text-blue-300-4); background: var(--ts-bg-blue-400-a24); }
    .tone-green { --stat-accent: #15803d; } .tone-green .icon { color: var(--ts-text-green-350-2); background: var(--ts-bg-green-500-a22); }
    .tone-orange { --stat-accent: #b45309; } .tone-orange .icon { color: var(--ts-text-amber-350); background: var(--ts-bg-orange-450-a22); }
    @media (max-width: 1100px) { .stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 480px) {
      .stats { gap: .6rem; }
      .stat { min-height: 84px; padding: .75rem; gap: .6rem; }
      .icon { width: 38px; height: 38px; flex-basis: 38px; font-size: 1rem; }
      strong { font-size: 1.25rem; }
    }
  `],
})
export class DashboardStatsComponent implements OnChanges {
  @Input() reservationsToday: number | null = null;
  @Input() reservationsYesterday: number | null = null;
  @Input() spacesActive: number | null = null;
  @Input() spacesTotal: number | null = null;
  @Input() customersTotal: number | null = null;
  @Input() paymentsPending: number | null = null;
  @Input() loading = false;

  @Input() canReservations = true;
  @Input() canSpaces = true;
  @Input() canCustomers = true;

  @Output() select = new EventEmitter<StatKey>();

  /**
   * Array cacheado: un getter que devolviera un arreglo nuevo en cada pasada de
   * detección haría que *ngFor entrara en un bucle infinito de render.
   */
  private view: StatView[] = [];

  ngOnChanges(): void {
    this.view = this.build();
  }

  get stats(): StatView[] {
    return this.view;
  }

  private build(): StatView[] {
    return [
      {
        key: 'reservations',
        label: 'Reservas hoy',
        value: this.value(this.reservationsToday),
        detail: this.delta(),
        icon: 'bi-calendar2-check',
        tone: 'violet',
        arrow: false,
        visible: this.canReservations,
      },
      {
        key: 'spaces',
        label: 'Espacios operativos',
        value: this.value(this.spacesActive),
        detail: this.spacesDetail(),
        icon: 'bi-door-open',
        tone: 'blue',
        arrow: false,
        visible: this.canSpaces,
      },
      {
        key: 'customers',
        label: 'Clientes registrados',
        value: this.value(this.customersTotal),
        detail: 'Ver directorio',
        icon: 'bi-people',
        tone: 'green',
        arrow: true,
        visible: this.canCustomers,
      },
      {
        key: 'payments',
        label: 'Pagos pendientes',
        value: this.value(this.paymentsPending),
        detail: (this.paymentsPending ?? 0) > 0 ? 'Revisar pagos' : 'Sin cobros pendientes',
        icon: 'bi-cash-coin',
        tone: 'orange',
        arrow: (this.paymentsPending ?? 0) > 0,
        visible: this.canReservations,
      },
    ];
  }

  private value(input: number | null): string {
    return input === null || input === undefined ? '—' : String(input);
  }

  /** Comparación real con el día anterior (solo si ambos datos existen). */
  private delta(): string {
    const today = this.reservationsToday;
    const yesterday = this.reservationsYesterday;
    if (today === null || yesterday === null) return 'Ocupación del día';
    const diff = today - yesterday;
    if (diff === 0) return 'Igual que ayer';
    return diff > 0 ? `${diff} más que ayer` : `${Math.abs(diff)} menos que ayer`;
  }

  private spacesDetail(): string {
    const total = this.spacesTotal;
    const active = this.spacesActive;
    if (active === null) return 'Del catálogo actual';
    if (total !== null && total > active) {
      const unavailable = total - active;
      return `${active} de ${total} · ${unavailable} no ${unavailable === 1 ? 'operativo' : 'operativos'}`;
    }
    return total === null ? 'Catálogo actual' : `${active} de ${total} operativos`;
  }
}
